import { useState } from "react"
import {
  Box,
  Grid,
  Typography,
  IconButton,
  ClickAwayListener,
  styled,
  Tooltip,
  useTheme
} from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleInfo, faXmark } from "@fortawesome/free-solid-svg-icons"

const StyledToolTip = styled(Tooltip)(({ theme }) => ({
  "& .MuiTooltip-tooltip": {
    backgroundColor: "(theme.palette as any).foreground.inverted",
    color: theme.palette.text.primary
  },
  "& .MuiTooltip-arrow": {
    color: (theme.palette as any).foreground.inverted
  },
  position: "relative"
}))

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  right: "0",
  top: "18px"
}))

const StyledFontAwesomeXmark = styled(FontAwesomeIcon)(({ theme }) => ({
  width: "20px",
  height: "20px"
}))

const StyledInfoIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== "iconPosition"
})<{ iconPosition?: boolean }>(({ theme, iconPosition }) => ({
  ...(iconPosition && {
    position: "absolute",
    right: "-6px",
    top: "-4px",
    padding: 0
  })
}))

export const DynamicTooltip = ({
  title,
  children,
  iconColor,
  t,
  subtitle,
  iconPosition
}: {
  title?: string
  children: React.ReactNode
  iconColor?: string
  t: any
  subtitle?: string
  iconPosition?: boolean
}) => {
  const [open, setOpen] = useState(false)

  const handleTooltipClose = () => setOpen(false)
  const handleTooltipOpen = () => setOpen(true)
  const theme = useTheme()

  return (
    <Box component="span" sx={iconPosition ? { position: "relative", display: "inline" } : undefined}>
      <ClickAwayListener onClickAway={handleTooltipClose}>
        <StyledToolTip
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                boxShadow: "0px 0px 65px 0px rgb(0 0 0 / 6%)"
              }
            }
          }}
          onClose={handleTooltipClose}
          open={open}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          title={
            <CustomTooltipContent subtitle={subtitle} title={title} handleTooltipClose={handleTooltipClose}>
              {children}
            </CustomTooltipContent>
          }>
          <StyledInfoIconButton onClick={handleTooltipOpen} size="small" iconPosition={iconPosition}>
            <FontAwesomeIcon
              style={{ color: iconColor ?? (theme.palette as any).primary[700] }}
              icon={faCircleInfo}
            />
          </StyledInfoIconButton>
        </StyledToolTip>
      </ClickAwayListener>
    </Box>
  )
}

const CustomTooltipContent = ({
  handleTooltipClose,
  title,
  subtitle,
  children
}: {
  handleTooltipClose: () => void
  title?: string
  subtitle?: string
  children: React.ReactNode
}) => (
  <Grid container direction="column" spacing={2} p={2} position="relative">
    <StyledIconButton onClick={handleTooltipClose} size="small">
      <StyledFontAwesomeXmark icon={faXmark} />
    </StyledIconButton>

    <Grid item>
      {title && <Typography variant="h4">{title}</Typography>}
      {subtitle && (
        <Box pt={0.5}>
          <Typography variant="bodySmall" color="textSecondary">
            {subtitle}
          </Typography>
        </Box>
      )}
    </Grid>

    {children}
  </Grid>
)
